import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { clearSelectedMedia } from '@ports/common/commonStateSlice';
import MediaPlayer, {
  MediaType
} from '@business/workflows/ComponentUI/MediaPlayer';
import {
  useGetCountsQuery,
  useGetLikesQuery,
  useAddLikesMutation,
  useDeleteLikesMutation
} from '@ports/internal-services/likes/likesSlice';
import {
  useGetCommentsQuery,
  useAddCommentsMutation,
  useUpdateCommentsMutation,
  useDeleteCommentsMutation
} from '@ports/internal-services/comment/commentSlice';
import {
  useDeleteAssetsMutation,
  useGetAssetsByIdQuery
} from '@ports/internal-services/asset/assetSlice';
import { useAddFavoritesMutation } from '@ports/internal-services/favorites/favoritesSlice';
import { getUserData } from '@ports/common/userStateSlice';
import { getSubscriberId } from '@ports/common/sessionStateSlice';
import { Comment, MediaPostScreenProps } from '@business/interfaces/photo';
import { ConfirmationModal } from '@business/workflows/ComponentUI';
import { handleDownload } from '@utils/helpers/lib/photoHelpers';
import { routePaths } from '@utils/constants/routePaths';
import MediaPostHeader from './MediaPostHeader';
import MediaStats from './MediaStats';
import CommentSection from './CommentSection';
import FullScreenModal from './FullScreenModal';
import ShareModal from './ShareModal';
import MediaInfoSidebar from './MediaInfoSidebar';
import DiarySection from './DiarySection';

const MediaPostScreen: React.FC<MediaPostScreenProps> = ({ assetId }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userDetails = useSelector(getUserData);
  const userId = userDetails.userId || ''; // Using email as userId, adjust as needed
  const subscriberId = useSelector(getSubscriberId);

  // Fetch asset data
  const { data: assetData, refetch: refetchAsset } = useGetAssetsByIdQuery(
    { id: assetId },
    { skip: !assetId }
  );

  // Fetch counts (likes, comments, diary)
  const { data: countsData, refetch: refetchCounts } = useGetCountsQuery(
    { assetId },
    { skip: !assetId }
  );

  // Fetch user's like status
  const { data: likesData, refetch: refetchLikes } = useGetLikesQuery(
    { assetId },
    { skip: !assetId }
  );

  // Fetch comments
  const { data: commentsData, refetch: refetchComments } = useGetCommentsQuery(
    { assetId },
    { skip: !assetId }
  );

  // Mutations
  const [addLike] = useAddLikesMutation();
  const [deleteLike] = useDeleteLikesMutation();
  const [addComment] = useAddCommentsMutation();
  const [updateComment] = useUpdateCommentsMutation();
  const [deleteComment] = useDeleteCommentsMutation();
  const [deleteAsset] = useDeleteAssetsMutation();
  const [addFavorite] = useAddFavoritesMutation();

  // Local state for optimistic updates
  const [isLiked, setIsLiked] = useState(false);
  const [userLikeId, setUserLikeId] = useState<string | null>(null);
  const [optimisticLikeCount, setOptimisticLikeCount] = useState(0);
  const [optimisticCommentCount, setOptimisticCommentCount] = useState(0);
  const [optimisticDiaryCount, setOptimisticDiaryCount] = useState(0);
  const [optimisticComments, setOptimisticComments] = useState<Comment[]>([]);
  const [isProcessingLike, setIsProcessingLike] = useState<boolean>(false);
  const [isFullScreenOpen, setIsFullScreenOpen] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState<boolean>(false);
  const [isInfoSidebarOpen, setIsInfoSidebarOpen] = useState<boolean>(false);
  const [isDiarySectionOpen, setIsDiarySectionOpen] = useState<boolean>(false);
  const [isCommentSectionOpen, setIsCommentSectionOpen] =
    useState<boolean>(true);
  const [zoomLevel, setZoomLevel] = useState<number>(1);
  const [isFavorite, setIsFavorite] = useState<boolean>(false);

  // Extract media data from asset
  const mediaType = assetData?.data?.contentType || 'image';
  const mediaSrc = assetData?.data?.url || '';

  // Update favorite status when asset data is fetched
  useEffect(() => {
    if (assetData?.data?.favorite) {
      setIsFavorite(assetData?.data?.favorite?.status === 'Active');
    }
  }, [assetData]);

  // Update like status when data is fetched
  useEffect(() => {
    if (likesData?.data) {
      setIsLiked(likesData.data.count ? likesData.data.count > 0 : false);
    }
  }, [likesData]);

  // Update optimistic counts when real data arrives
  useEffect(() => {
    if (countsData?.data) {
      setOptimisticLikeCount(countsData.data.likes || 0);
      setOptimisticCommentCount(countsData.data.comments || 0);
      setOptimisticDiaryCount(countsData.data.diary || 0);
    }
  }, [countsData]);

  // Transform comments data to match Comment interface
  const transformedComments: Comment[] =
    commentsData?.data?.rows?.map(comment => ({
      id: comment.id,
      name: comment.name,
      profileImage: comment.profileImage,
      text: comment.comment,
      timestamp: new Date(comment.createdAt).toLocaleString(),
      userId: comment.userId,
      commentLikesCount: comment.commentLikesCount || 0,
      commentLikeCount: comment.commentLikesCount || 0,
      commentLikes: comment.commentLikes || [],
      isLiked: false,
      userCommentLikeId: null as string | null
    })) || [];

  // Update comments when data is fetched
  useEffect(() => {
    setOptimisticComments(transformedComments);
  }, [commentsData]);

  const displayComments = optimisticComments;

  const handleCommentLikeUpdate = () => {
    // Just refetch comments to get updated like counts
    // The child component already handles the API call and optimistic updates
    setTimeout(() => {
      refetchComments();
    }, 100);
  };

  const handleDiaryUpdate = () => {
    // Refetch counts when diary is added/deleted
    setTimeout(() => {
      refetchCounts();
    }, 100);
  };

  const handleFavorite = async () => {
    const previousIsFavorite = isFavorite;

    try {
      // Optimistic update
      setIsFavorite(!isFavorite);

      // Call API
      const status = !isFavorite ? 'Active' : 'InActive';
      await addFavorite({
        createFavorites: {
          userId,
          assetId,
          subscriberId,
          status
        }
      }).then(() => {
        refetchAsset();
      });
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
      // Rollback on failure
      setIsFavorite(previousIsFavorite);
    }
  };

  const handleLike = async () => {
    // Prevent multiple rapid clicks
    if (isProcessingLike) return;
    setIsProcessingLike(true);

    // Store previous state for rollback
    const previousIsLiked = isLiked;
    const previousCount = optimisticLikeCount;
    const previousLikeId = userLikeId;

    try {
      // 1. OPTIMISTIC UPDATE - Update UI immediately
      setIsLiked(!isLiked);
      setOptimisticLikeCount(prev => (isLiked ? prev - 1 : prev + 1));

      if (isLiked && userLikeId) {
        // Unlike
        await deleteLike({ id: userLikeId, userId, assetId }).unwrap();
        setUserLikeId(null);
      } else {
        // Like
        const result = await addLike({
          createLikes: { assetId, userId }
        }).unwrap();
        if (result.data?.id) {
          setUserLikeId(result.data.id);
        }
      }

      // 2. BACKGROUND REFRESH - Fetch real data without blocking UI
      setTimeout(() => {
        refetchCounts();
        refetchLikes();
      }, 100);
    } catch (error) {
      console.error('Failed to toggle like:', error);

      // 3. ROLLBACK on failure
      setIsLiked(previousIsLiked);
      setOptimisticLikeCount(previousCount);
      setUserLikeId(previousLikeId);
    } finally {
      // Allow next click after a short delay
      setTimeout(() => setIsProcessingLike(false), 300);
    }
  };

  const handleAddComment = async (text: string) => {
    try {
      // API call first, wait for success
      await addComment({
        createComments: {
          userId,
          assetId,
          comment: text
        }
      }).unwrap();

      // Refetch comments after successful API call
      refetchComments();
      refetchCounts();
    } catch (error) {
      console.error('Failed to add comment:', error);
    }
  };

  const handleUpdateComment = async (commentId: string, text: string) => {
    try {
      await updateComment({
        id: commentId,
        updateComments: {
          comment: text
        }
      }).unwrap();

      // Refetch comments after successful update
      refetchComments();
    } catch (error) {
      console.error('Failed to update comment:', error);
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    // Store previous state for rollback
    const previousComments = [...optimisticComments];
    const previousCount = optimisticCommentCount;

    try {
      // 1. OPTIMISTIC UPDATE - Remove comment immediately
      setOptimisticComments(prev =>
        prev.filter(comment => comment.id !== commentId)
      );
      setOptimisticCommentCount(prev => prev - 1);

      // 2. API CALL
      await deleteComment({ id: commentId, delete: 'true' }).unwrap();

      // 3. BACKGROUND REFRESH
      setTimeout(() => {
        refetchComments();
        refetchCounts();
      }, 100);
    } catch (error) {
      console.error('Failed to delete comment:', error);

      // ROLLBACK - Restore deleted comment
      setOptimisticComments(previousComments);
      setOptimisticCommentCount(previousCount);
    }
  };

  const handleBack = () => {
    dispatch(clearSelectedMedia());
    navigate(routePaths.Photos);
  };

  const handleExpand = () => {
    setIsFullScreenOpen(true);
  };

  const handleZoomIn = () => {
    if (mediaType === 'image') {
      setZoomLevel(prev => Math.min(prev + 0.25, 3));
    }
  };

  const handleZoomOut = () => {
    if (mediaType === 'image') {
      setZoomLevel(prev => Math.max(prev - 0.25, 0.5));
    }
  };

  const handleShare = () => {
    setIsShareModalOpen(true);
  };

  const handleDiaryClick = () => {
    setIsDiarySectionOpen(!isDiarySectionOpen);
    if (!isDiarySectionOpen) {
      setIsCommentSectionOpen(false);
    }
  };

  const handleCommentClick = () => {
    setIsCommentSectionOpen(!isCommentSectionOpen);
    if (!isCommentSectionOpen) {
      setIsDiarySectionOpen(false);
    }
  };

  const handleShareConfirm = () => {
    setIsShareModalOpen(false);
  };

  const handleInfo = () => {
    setIsInfoSidebarOpen(true);
  };

  const handleStar = () => {
    handleFavorite();
  };

  const handleDeleteClick = () => {
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteAsset({ id: assetId }).unwrap();

      // Close modal
      setIsDeleteModalOpen(false);

      // Clear selected media from Redux
      dispatch(clearSelectedMedia());

      // Navigate back to photos page
      navigate(routePaths.Photos);
    } catch (error) {
      console.error('Failed to delete asset:', error);
      // Keep modal open so user can try again or cancel
      // Optionally show error toast/alert here
    }
  };

  return (
    <div className="min-h-screen bg-[var(--media-page-bg)] text-gray-100">
      <MediaPostHeader
        onBack={handleBack}
        onExpand={handleExpand}
        onShare={handleShare}
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        onInfo={handleInfo}
        onStar={handleStar}
        onDelete={handleDeleteClick}
        onDownload={() => handleDownload(mediaSrc)}
        mediaType={mediaType as MediaType}
        isFavorite={isFavorite}
      />

      <main className="max-w-6xl mx-auto px-6 pb-10">
        <section className="bg-[var(--media-comment-bg)] rounded-md overflow-hidden shadow-lg">
          <div className="relative bg-media-input">
            <div
              className={`w-full h-auto ${mediaType === 'video' ? 'min-h-[300px]' : ''} bg-media-input flex items-center justify-center overflow-hidden`}>
              <div
                style={{
                  transform:
                    mediaType === 'image' ? `scale(${zoomLevel})` : 'none',
                  transition: 'transform 0.2s ease-in-out',
                  width: '100%',
                  display: 'block'
                }}>
                <MediaPlayer
                  type={mediaType}
                  src={mediaSrc}
                  controls={true}
                  muted={true}
                  width="100%"
                  height="auto" // CHANGED: Allow height to scale based on width
                  // CHANGED: w-full ensures stretch, h-auto maintains aspect ratio
                  containerClassName="w-full h-auto block"
                />
              </div>
            </div>
          </div>

          <MediaStats
            likes={optimisticLikeCount}
            comments={optimisticCommentCount}
            shares={optimisticDiaryCount}
            isLiked={isLiked}
            onLike={handleLike}
            onComment={handleCommentClick}
            onShare={handleDiaryClick}
          />
        </section>
        {isCommentSectionOpen && (
          <CommentSection
            comments={displayComments}
            currentUserId={userId}
            onAddComment={handleAddComment}
            onUpdateComment={handleUpdateComment}
            onDeleteComment={handleDeleteComment}
            onCommentLikeUpdate={handleCommentLikeUpdate}
          />
        )}

        {isDiarySectionOpen && (
          <DiarySection
            assetId={assetId}
            userId={userId}
            isOpen={isDiarySectionOpen}
            onDiaryUpdate={handleDiaryUpdate}
          />
        )}
      </main>

      {/* Full Screen Modal */}
      <FullScreenModal
        isOpen={isFullScreenOpen}
        onClose={() => setIsFullScreenOpen(false)}
        mediaType={mediaType}
        mediaSrc={mediaSrc}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
        variant="delete"
      />

      {/* Share Modal */}
      <ShareModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        onShare={handleShareConfirm}
        link={mediaSrc || ''}
      />

      {/* Info Sidebar */}
      <MediaInfoSidebar
        isOpen={isInfoSidebarOpen}
        onClose={() => setIsInfoSidebarOpen(false)}
        onReopen={() => setIsInfoSidebarOpen(true)}
        assetId={assetId}
      />
    </div>
  );
};

export default MediaPostScreen;
